import React from 'react';
import { AreaChart, Area, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { useClient } from '../../context/ClientContext';

export default function GoalPacing({ dailyTrend, summary, dateRange }) {
  const { brand } = useClient();
  const primaryColor = brand.colors?.primary || '#2563eb';

  if (dateRange !== 'mtd' || !summary?.target || !dailyTrend?.days?.length) return null;

  const target = summary.target;
  const now = new Date();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();

  // Build cumulative actual data
  const days = dailyTrend.days;
  let cumulative = 0;
  const dailyConversions = [];
  const actualData = days.map((d, i) => {
    cumulative += d.conversions || 0;
    dailyConversions.push(d.conversions || 0);
    return {
      day: i + 1,
      actual: cumulative,
      pace: Math.round(((i + 1) / daysInMonth) * target * 10) / 10
    };
  });

  const currentDay = actualData.length;

  // Calculate daily stats for forecast
  const mean = dailyConversions.length > 0
    ? dailyConversions.reduce((a, b) => a + b, 0) / dailyConversions.length
    : 0;
  const variance = dailyConversions.length > 1
    ? dailyConversions.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / (dailyConversions.length - 1)
    : 0;
  const stdDev = Math.sqrt(variance);

  // Project forward from current day to end of month
  const remainingDays = daysInMonth - currentDay;
  const projectedTotal = Math.round(cumulative + mean * remainingDays);
  const dailyNeeded = remainingDays > 0 ? Math.max(0, (target - cumulative) / remainingDays) : 0;

  // Build forecast data
  const forecastData = [];
  for (let d = currentDay; d <= daysInMonth; d++) {
    const daysAhead = d - currentDay;
    const projected = cumulative + mean * daysAhead;
    const band = stdDev * Math.sqrt(daysAhead);
    forecastData.push({
      day: d,
      projected: Math.round(projected * 10) / 10,
      upper: Math.round((projected + band) * 10) / 10,
      lower: Math.round(Math.max(0, projected - band) * 10) / 10,
      pace: Math.round((d / daysInMonth) * target * 10) / 10
    });
  }

  // Combine for chart
  const chartData = [];
  for (let d = 1; d <= daysInMonth; d++) {
    const actual = actualData.find(a => a.day === d);
    const forecast = forecastData.find(f => f.day === d);
    chartData.push({
      day: d,
      actual: actual?.actual ?? null,
      pace: Math.round((d / daysInMonth) * target * 10) / 10,
      projected: forecast?.projected ?? null,
      upper: forecast?.upper ?? null,
      lower: forecast?.lower ?? null
    });
  }

  // Status colour
  const paceRatio = projectedTotal / target;
  const statusColor = paceRatio >= 0.9 ? '#10b981' : paceRatio >= 0.7 ? '#f59e0b' : '#ef4444';

  return (
    <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 p-6">
      <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1"
          style={{ fontFamily: brand.typography?.headerFont }}>
        Goal Pacing
      </h3>
      <p className="text-xs text-gray-400 mb-4">Cumulative conversions vs target pace</p>

      <ResponsiveContainer width="100%" height={250}>
        <AreaChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
          <defs>
            <linearGradient id="forecastBand" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={primaryColor} stopOpacity={0.1} />
              <stop offset="95%" stopColor={primaryColor} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="day"
            tick={{ fontSize: 11, fill: '#666' }}
            tickLine={false}
            axisLine={{ stroke: '#e5e7eb' }}
            interval="preserveStartEnd"
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#666' }}
            tickLine={false}
            axisLine={false}
            allowDecimals={false}
          />
          <Tooltip
            formatter={(val, name) => {
              if (val == null) return [null, null];
              const labels = { actual: 'Actual', pace: 'Target Pace', projected: 'Projected', upper: 'Upper', lower: 'Lower' };
              return [Math.round(val), labels[name] || name];
            }}
            contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb' }}
          />
          {/* Target pace line */}
          <Line
            type="monotone"
            dataKey="pace"
            stroke="#9ca3af"
            strokeWidth={1.5}
            strokeDasharray="6 3"
            dot={false}
            isAnimationActive={false}
          />
          {/* Forecast band */}
          <Area
            type="monotone"
            dataKey="upper"
            stroke="none"
            fill="url(#forecastBand)"
            isAnimationActive={false}
          />
          <Area
            type="monotone"
            dataKey="lower"
            stroke="none"
            fill="#ffffff"
            isAnimationActive={false}
          />
          {/* Projected line */}
          <Line
            type="monotone"
            dataKey="projected"
            stroke={primaryColor}
            strokeWidth={1.5}
            strokeDasharray="4 2"
            strokeOpacity={0.5}
            dot={false}
            isAnimationActive={false}
          />
          {/* Actual cumulative line */}
          <Line
            type="monotone"
            dataKey="actual"
            stroke={primaryColor}
            strokeWidth={2.5}
            dot={false}
            activeDot={{ r: 4 }}
            connectNulls={false}
          />
          <ReferenceLine y={target} stroke="#9ca3af" strokeDasharray="3 3" strokeOpacity={0.5} />
        </AreaChart>
      </ResponsiveContainer>

      <div className="flex gap-4 mt-2 text-xs text-gray-500 justify-center">
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-0.5 rounded" style={{ backgroundColor: primaryColor }}></span>
          Actual
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-0.5 rounded bg-gray-400" style={{ borderBottom: '1px dashed #9ca3af' }}></span>
          Target Pace
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-0.5 rounded" style={{ backgroundColor: primaryColor, opacity: 0.5 }}></span>
          Projected
        </span>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-100">
        <div className="text-center">
          <p className="text-lg font-bold" style={{ color: statusColor }}>
            {projectedTotal}
          </p>
          <p className="text-xs text-gray-500">Projected Total</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-bold text-gray-900">{remainingDays}</p>
          <p className="text-xs text-gray-500">Days Left</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-bold text-gray-900">{dailyNeeded.toFixed(1)}</p>
          <p className="text-xs text-gray-500">Daily Rate Needed</p>
        </div>
      </div>
    </div>
  );
}
